import React, { useMemo, useState } from 'react';
import {
  VerticalTimeline,
  VerticalTimelineElement,
} from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { BRAND_COLORS } from '../../config/brand';
import { TIMELINE_FILTER } from './timeline.constants';
import type { TimelineFilterType, TimelineType } from './timeline.types';
import './Timeline.css';
import { default as FilterListOutlined } from '../../assets/FilterListOutlined';
import { getOrdinal } from '../../helpers/functions';
import priceFormat from '../../helpers/priceFormat';
import { Button, Card, Divider } from '../../library';
import { Tooltip } from '../Tooltip';
import TimeLineFilter from './TimeLineFilter';

interface Installment {
  id: number;
  installmentNumber?: number;
}

const getInstallmentNumber = (
  id: string,
  installments: Installment[] | undefined,
  lng?: string
) => {
  let index = installments?.findIndex((e) => e.id === Number(id));
  if (index !== undefined) {
    index += 1;
    if (lng === 'en') {
      return getOrdinal({ number: index });
    }
    return index;
  }
  return id;
};

interface TimelineProps {
  timelineDetails: TimelineType[];
  loading: boolean;
  type: 'order' | 'subscription';
  isPayNow?: boolean;
  currency?: string;
  installments?: Installment[];
}

const Timeline = ({
  timelineDetails,
  loading,
  type,
  isPayNow = false,
  currency,
  installments,
}: TimelineProps) => {
  const { t, i18n } = useTranslation();
  const [timelineFilterOptions, setTimelineFilterOptions] = useState<
    TimelineFilterType[]
  >(TIMELINE_FILTER[type]);

  const filteredOrderTimeline = useMemo(() => {
    const flattenedFilter = timelineFilterOptions.reduce(
      (acc: string[], curr) => {
        if (curr.value) {
          return acc.concat(curr.actionNames);
        }
        return acc;
      },
      []
    );

    const filteredData = timelineDetails || [];
    return filteredData?.filter((timelineDetail) =>
      flattenedFilter.includes(timelineDetail.actionName)
    );
  }, [timelineFilterOptions, timelineDetails]);

  const handleTimelineFilter = ({
    label,
    value,
  }: {
    label: string;
    value: boolean;
  }) => {
    const newFilterOptions = timelineFilterOptions.map((option) => {
      if (label === option.label) {
        return { ...option, value };
      }
      return option;
    });
    setTimelineFilterOptions(newFilterOptions);
  };

  if (!timelineDetails.length) {
    return null;
  }

  return (
    <div className="mt-3">
      {/* mobile view */}
      <div className="d-flex flex-row justify-content-between">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between">
            <h3 className="!text-[18px] font-bold">{t(`timeline.${type}.title`)}</h3>

            <div className="d-flex d--md-none">
              <Tooltip
                trigger="click"
                content={
                  <Card>
                    <TimeLineFilter
                      setTimelineFilterOptions={setTimelineFilterOptions}
                      timelineFilterOptions={timelineFilterOptions}
                      handleTimelineFilter={handleTimelineFilter}
                      type={type}
                    />
                  </Card>
                }
              >
                <Button variant="text">
                  <FilterListOutlined className="tl-filter-icon" />
                </Button>
              </Tooltip>
            </div>
          </div>
          <Divider />
        </div>
      </div>
      <div className="row d-flex justify-content-between mt-1 ps-half ml-2">
        <div className="col p-0 ml-2">
          {loading && (
            <h3>
              {t('loading')}
              ...
            </h3>
          )}
          {!!filteredOrderTimeline?.length && (
            <VerticalTimeline
              className="verticalTimeline"
              layout={
                i18n.language === 'ar' ? '1-column-right' : '1-column-left'
              }
              lineColor={BRAND_COLORS.soft}
            >
              {filteredOrderTimeline?.map((timelineDetail) => (
                <VerticalTimelineElement
                  key={timelineDetail.id}
                  className="verticalTimelineElementContainer"
                  textClassName="verticalTimelineElement"
                  iconClassName="verticalTimelineIcon"
                >
                  <h3 className="!text-[14px] font-semibold" style={{ color: '#A4A4A4' }}>
                    {moment(timelineDetail.createdAt)
                      .locale(i18n.language || 'en')
                      .format('DD MMM YYYY, h:mm A')}
                  </h3>
                  <p>
                    <span className="text-[13px] font-bold">
                      {t(
                        `${type}.timeline.${
                          timelineDetail.actionName === 'ORDER_PAYMENT' &&
                          isPayNow
                            ? 'PAY_NOW_ORDER_PAYMENT'
                            : timelineDetail.actionName
                        }`,
                        {
                          reason:
                            timelineDetail.additionalInfo?.rejectionReason,
                          instalments: timelineDetail.additionalInfo
                            ?.installments
                            ? timelineDetail.additionalInfo?.installments
                                .map((e) =>
                                  getInstallmentNumber(
                                    e,
                                    installments,
                                    i18n.language
                                  )
                                )
                                .join(',')
                            : '',
                          card: timelineDetail.additionalInfo?.cardNumber || '',
                          ordinal: getOrdinal({
                            number:
                              timelineDetail?.additionalInfo
                                ?.installmentNumber ||
                              timelineDetail?.additionalInfo?.installment?.[0]
                                ?.installmentNumber ||
                              1,
                          }),
                          currency,
                          amount: priceFormat({
                            amount: Number(
                              (timelineDetail?.additionalInfo?.refundAmount ||
                                0) +
                                (timelineDetail?.additionalInfo
                                  ?.purchaseFeeRefundAmount || 0)
                            ),
                          }),
                        }
                      )}
                    </span>
                    <span className="!text-[14px] font-semibold px-1" style={{ color: '#A4A4A4' }}>
                      {' '}
                      {t('by')}{' '}
                    </span>
                    <span className="text-[13px] font-bold">
                      {timelineDetail?.createdBy?.type !== 'SYSTEM' &&
                      timelineDetail?.createdBy?.email
                        ? `${t(`${type}.timeline.${timelineDetail?.createdBy?.type}`)} - ${timelineDetail?.createdBy?.email}`
                        : t(
                            `${type}.timeline.${timelineDetail?.createdBy?.type}`
                          )}
                    </span>
                  </p>
                </VerticalTimelineElement>
              ))}
            </VerticalTimeline>
          )}
        </div>

        <div className="col-12 col-md-4 col-xl-3 d-none d--md-block">
          <Card>
            <TimeLineFilter
              setTimelineFilterOptions={setTimelineFilterOptions}
              timelineFilterOptions={timelineFilterOptions}
              handleTimelineFilter={handleTimelineFilter}
              type={type}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Timeline;
